import { FC, useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  Autocomplete,
  Avatar,
  Box,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { PersonOutline } from "@mui/icons-material";
import useSWR from "swr";
import { TSeller, getSellers, getSellerById } from "@/api";
import useDebounce from "@/hooks/debounce";

const buildTitle = (s: TSeller | undefined) => {
  if (!s) return "";
  const name = [s.firstName, s.lastName].filter(Boolean).join(" ").trim();
  if (name) return name;
  if (s.email) return s.email;
  if ((s as any).documentNumber) return String((s as any).documentNumber);
  return s.id;
};

/**
 * ClientAutocomplete — v2 client picker. Mirrors the suppliers-web
 * ClientCombobox: single text query (`text:<input>`) matches across
 * name / email / document, results show multiple fields per row.
 */
export const ClientAutocomplete: FC = () => {
  const { setValue } = useFormContext();
  const sellerId = useWatch({ name: "sellerId" }) as string | undefined;

  const [input, setInput] = useState("");
  const debounced = useDebounce(input, 300);

  const { data, isValidating } = useSWR(
    ["clients-v2-search", debounced],
    () =>
      getSellers(
        debounced
          ? ({ search: `text:${debounced}`, size: 50 } as any)
          : ({ size: 50 } as any)
      ),
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  const { data: selected } = useSWR(
    sellerId ? ["seller-by-id", sellerId] : null,
    () => getSellerById(sellerId!),
    { revalidateOnFocus: false }
  );

  const options: TSeller[] = useMemo(() => {
    const list = data?.content ?? [];
    if (selected && !list.find((s: TSeller) => s.id === selected.id)) {
      return [selected, ...list];
    }
    return list;
  }, [data, selected]);

  useEffect(() => {
    if (selected) {
      setValue("userType", (selected as any).userType, { shouldDirty: false });
    }
  }, [selected, setValue]);

  return (
    <Autocomplete<TSeller, false, false, false>
      value={selected ?? null}
      onChange={(_, value) => {
        setValue("sellerId", value?.id ?? "", {
          shouldDirty: true,
          shouldValidate: true,
        });
      }}
      onInputChange={(_, v) => setInput(v)}
      options={options}
      getOptionLabel={(o) => buildTitle(o)}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      filterOptions={(x) => x}
      loading={isValidating}
      noOptionsText={
        debounced ? "Sin resultados" : "Escribí para buscar un cliente"
      }
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.5 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: "action.hover",
                color: "text.secondary",
                fontSize: 14,
              }}
            >
              {(option.firstName?.[0] ?? option.email?.[0] ?? "?").toUpperCase()}
            </Avatar>
            <Stack spacing={0} sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {buildTitle(option)}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ color: "text.secondary", minWidth: 0 }}
              >
                {option.email && (
                  <Typography
                    variant="caption"
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 220,
                    }}
                  >
                    {option.email}
                  </Typography>
                )}
                {(option as any).documentNumber && (
                  <>
                    <Typography variant="caption" color="text.disabled">
                      ·
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {(option as any).documentNumber}
                    </Typography>
                  </>
                )}
              </Stack>
            </Stack>
          </Stack>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Buscar cliente"
          placeholder="Nombre, correo o documento…"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <Box sx={{ display: "flex", alignItems: "center", ml: 0.5, mr: 0.5, color: "text.secondary" }}>
                  <PersonOutline fontSize="small" />
                </Box>
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {isValidating ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      fullWidth
    />
  );
};
